import {
  OfferDataTableElementKey,
  OfferDataTableGroupKey,
  OfferTableElement
} from '@/components/calculator/results/offers/OfferTableElement';
import {
  BridgingInsurance,
  Cost,
  InsurancePackage,
  LifeInsurance,
  Offer,
  PaymentInsurance,
  RealEstateInsurance
} from '@/models/simulation/Simulation';
import {numeralFormat} from '@/filters/StringFilters';
import {
  CreditCard,
  getInsuranceElement,
  InterestRateSum, MarginRow,
  Provision
} from '@/components/calculator/results/tableElements/CommonTableElements';
import {MortgageSimulationDemand, SimulationDemand} from '@/models/Demand';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {OnboardingService} from '@/modules/onboarding/OnboardingService';
import {Tour} from '@/modules/onboarding/tour/Tour';
import {EMPTY_VALUE, TableElementHeight} from '@/components/calculator/results/tableElements/TableElementsConstants';
import {useSimulationCurrency} from '@/components/calculator/useSimulationCurrency';
import {renderProvision} from '@/components/calculator/results/tableElements/helpers/Provision';
import {
  renderInsurance,
  renderInsuranceIncludedInInitialCosts,
  renderInsuranceIncludedInTransitionalInstallment
} from '@/components/calculator/results/tableElements/helpers/XSell';
import EnvironmentService from '@/env/EnvironmentService';
import {isNumber} from 'lodash-es';
import {renderDTI} from '@/components/calculator/results/tableElements/helpers/DTI';
import AuthService from '@/modules/user/AuthService';
import {
  getGovProgramInfo,
  getTotalSubsidyRow
} from '@/components/calculator/results/tableElements/helpers/GovProgramInfo';

export type InsuranceType = BridgingInsurance | RealEstateInsurance |
  LifeInsurance | InsurancePackage | PaymentInsurance;

const currentCurrency = EnvironmentService.Environment.currentCurrency();
const renderFirstInstallment = (x: Offer, userInput?: Nullable<SimulationDemand>) =>
  (`${numeralFormat(x.monthlyFees.firstInstallment)} ${currentCurrency}` +
    (x.fxValues ? ` (${x.fxValues.firstInstallment} ${userInput?.currency})` : '') + '&nbsp' + ((x.features.isSafeCredit2Percent || x.features.isHomeStartProgram) ? `(${I18NGetter().useMortgageTableElements.AFTER_SUBSIDY})` : '')
  );

const mortgageCalculatorTour: Tour = new OnboardingService().tours.MORTGAGE_CALCULATOR;

const {isForeignCurrency,} = useSimulationCurrency();

const ableToRenderRrso = (): boolean => EnvironmentService.Environment.isAppDomain().NESTO_RO ? AuthService.User?.isAdminOrVerifier! : true;
export const MortgageTableElements: OfferTableElement[] = [
  {
    condition: (): boolean => EnvironmentService.Environment.isAppDomain().NESTO_RO,
    header: () => I18NGetter().useMortgageTableElements.DTI,
    height: TableElementHeight.INPUT,
    printHeight: TableElementHeight.DEFAULT,
    key: OfferDataTableElementKey.DTI,
    runtimeTemplate: true,
    render: (x: Offer) => renderDTI(x).header,
    open: true,
    children: [
      {
        header: () => '',
        key: OfferDataTableElementKey.DTI_DETAILS,
        render: (x: Offer) => renderDTI(x).details,
        height: 430,
      },
    ],
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_PAYMENT_SCHEDULE,
    height: TableElementHeight.INPUT,
    printHeight: TableElementHeight.DEFAULT,
    key: OfferDataTableElementKey.PAYMENT_SCHEDULE,
    runtimeTemplate: true,
    render: () => `
              <div>
                    <v-btn text outlined @click="openScheduleModal()">${I18NGetter().useHeaderContent.GENERATE}</v-btn>
              </div>`,
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_INITIAL_COSTS,
    key: OfferDataTableElementKey.INITIAL_COST,
    headerClass: mortgageCalculatorTour.step.INITIAL_COST,
    render: (x: Offer) => `<span class="font-weight-medium text-center">${numeralFormat(x.overview.initialCost)} ${currentCurrency}</span>`,
    open: false,
    children: [
      {
        header: () => I18NGetter().useHeaderContent.COMMISSION,
        key: OfferDataTableElementKey.INITIAL_COST_PROVISION,
        render: (x: Offer) => renderProvision(x),
      },
      {
        header: () => I18NGetter().useHeaderContent.REAL_ESTATE_INSURANCE,
        key: OfferDataTableElementKey.INITIAL_COST_REAL_ESTATE_INSURANCE,
        render: (x: Offer) => renderInsuranceIncludedInInitialCosts(x, 'realEstateInsurance'),
      },
      {
        header: () => I18NGetter().useHeaderContent.LIFE_INSURANCE,
        key: OfferDataTableElementKey.INITIAL_COST_LIFE_INSURANCE,
        render: (x: Offer) => renderInsuranceIncludedInInitialCosts(x, 'lifeInsurance'),
      },
      {
        header: () => I18NGetter().useHeaderContent.PAYMENT_INSURANCE,
        key: OfferDataTableElementKey.INITIAL_COST_PAYMENT_INSURANCE,
        render: (x: Offer) => renderInsuranceIncludedInInitialCosts(x, 'paymentInsurance'),
      },
      {
        header: () => I18NGetter().useHeaderContent.INSURANCE_PACKAGE,
        key: OfferDataTableElementKey.INITIAL_COST_INSURANCE_PACKAGE,
        render: (x: Offer) => renderInsuranceIncludedInInitialCosts(x, 'insurancePackage'),
      },
      {
        header: () => I18NGetter().useHeaderContent.REAL_ESTATE_APPRAISAL,
        key: OfferDataTableElementKey.INITIAL_COST_REAL_ESTATE_VALUATION,
        render: (x: Offer) => isNumber(x.initialCosts.realEstateAppraisal?.sum) ? `${numeralFormat(x.initialCosts.realEstateAppraisal?.sum)} ${currentCurrency}` : EMPTY_VALUE,
      },
    ],
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_TRANSITIONAL_INSTALLMENT_WITH_BRIDGE_INSURANCE,
    key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_WITH_BRIDGE_INSURANCE,
    height: TableElementHeight.INPUT,
    headerClass: mortgageCalculatorTour.step.TRANSITIONAL_INSTALLMENT_WITH_BRIDGE_INSURANCE,
    render: (x: Offer) => `<span class="font-weight-medium">${numeralFormat(x.overview.firstInstallment)} ${currentCurrency}</span> ${(x.features.isSafeCredit2Percent || x.features.isHomeStartProgram) ? `(${I18NGetter().useMortgageTableElements.AFTER_SUBSIDY})` : ''}`,
    open: false,
    children: [
      {
        header: () => I18NGetter().useHeaderContent.FIRST_INSTALLMENT,
        key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_WITH_BRIDGE_INSURANCE_INSTALLMENT,
        render: renderFirstInstallment,
      },
      {
        header: () => I18NGetter().useHeaderContent.BRIDGING_INSURANCE,
        key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_WITH_BRIDGE_INSURANCE_BRIDGING_INSURANCE,
        render: (x: Offer) => renderInsuranceIncludedInTransitionalInstallment(x, 'bridgingInsurance', true),
      },
      {
        header: () => I18NGetter().useHeaderContent.REAL_ESTATE_INSURANCE,
        key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_WITH_BRIDGE_INSURANCE_REAL_ESTATE_INSURANCE,
        render: (x: Offer) => renderInsuranceIncludedInTransitionalInstallment(x, 'realEstateInsurance', true),
      },
      {
        header: () => I18NGetter().useHeaderContent.LIFE_INSURANCE,
        key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_WITH_BRIDGE_INSURANCE_LIFE_INSURANCE,
        render: (x: Offer) => renderInsuranceIncludedInTransitionalInstallment(x, 'lifeInsurance', true),
      },
      {
        header: () => I18NGetter().useHeaderContent.PAYMENT_INSURANCE,
        key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_WITH_BRIDGE_INSURANCE_PAYMENT_INSURANCE,
        render: (x: Offer) => renderInsuranceIncludedInTransitionalInstallment(x, 'paymentInsurance', true),
      },
      {
        header: () => I18NGetter().useHeaderContent.LOW_PERSONAL_SHARE_INSURANCE,
        key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_WITH_BRIDGE_INSURANCE_LOW_PERSONAL_SHARE_INSURANCE,
        render: (x: Offer) => renderInsuranceIncludedInTransitionalInstallment(x, 'lowPersonalShareInsurance', true),
      },
      {
        header: () => I18NGetter().useHeaderContent.INSURANCE_PACKAGE,
        key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_WITH_BRIDGE_INSURANCE_INSURANCE_PACKAGE,
        render: (x: Offer) => renderInsuranceIncludedInTransitionalInstallment(x, 'insurancePackage', true),
      },
      {
        header: () => I18NGetter().useHeaderContent.BANK_ACCOUNT,
        key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_WITH_BRIDGE_INSURANCE_COSTS_SUM_PERSONAL_ACCOUNT,
        render: (x: Offer) => x.additionalProducts?.bankAccount?.fee! ? `${numeralFormat(x.additionalProducts?.bankAccount.fee)} ${currentCurrency}` : '-',
      },
    ],
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_TRANSITIONAL_INSTALLMENT,
    key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT,
    height: TableElementHeight.INPUT,
    headerClass: mortgageCalculatorTour.step.TRANSITIONAL_INSTALLMENT,
    render: (x: Offer) => `<span class="font-weight-medium">${numeralFormat(x.monthlyFees.bridgingInsurance ? x.overview.firstInstallment - x.monthlyFees.bridgingInsurance[0]?.fee : x.overview.firstInstallment)} ${currentCurrency}</span> ${x.features.isSafeCredit2Percent || x.features.isHomeStartProgram ? `(${I18NGetter().useMortgageTableElements.AFTER_SUBSIDY})` : ''}`,
    open: false,
    children: [
      {
        header: () => I18NGetter().useHeaderContent.FIRST_INSTALLMENT,
        key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_INSTALLMENT,
        render: renderFirstInstallment,
      },
      {
        header: () => I18NGetter().useHeaderContent.REAL_ESTATE_INSURANCE,
        key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_REAL_ESTATE_INSURANCE,
        render: (x: Offer) => renderInsuranceIncludedInTransitionalInstallment(x, 'realEstateInsurance', false),
      },
      {
        header: () => I18NGetter().useHeaderContent.LIFE_INSURANCE,
        key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_LIFE_INSURANCE,
        render: (x: Offer) => renderInsuranceIncludedInTransitionalInstallment(x, 'lifeInsurance', false),
      },
      {
        header: () => I18NGetter().useHeaderContent.PAYMENT_INSURANCE,
        key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_PAYMENT_INSURANCE,
        render: (x: Offer) => renderInsuranceIncludedInTransitionalInstallment(x, 'paymentInsurance', false),
      },
      {
        header: () => I18NGetter().useHeaderContent.LOW_PERSONAL_SHARE_INSURANCE,
        key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_LOW_PERSONAL_SHARE_INSURANCE,
        render: (x: Offer) => renderInsuranceIncludedInTransitionalInstallment(x, 'lowPersonalShareInsurance', false),
      },
      {
        header: () => I18NGetter().useHeaderContent.INSURANCE_PACKAGE,
        key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_INSURANCE_PACKAGE,
        render: (x: Offer) => renderInsuranceIncludedInTransitionalInstallment(x, 'insurancePackage', false),
      },
      {
        header: () => I18NGetter().useHeaderContent.BANK_ACCOUNT,
        key: OfferDataTableElementKey.TRANSITIONAL_INSTALLMENT_COSTS_SUM_PERSONAL_ACCOUNT,
        render: (x: Offer) => x.additionalProducts?.bankAccount?.fee ? `${numeralFormat(x.additionalProducts?.bankAccount.fee)} ${currentCurrency}` : '-',
      },
    ],
  },
  {

    header: () => I18NGetter().useMortgageTableElements.HEADER_TARGET_INSTALLMENT,
    key: OfferDataTableElementKey.TARGET_INSTALLMENT,
    height: TableElementHeight.INPUT,
    headerClass: mortgageCalculatorTour.step.TARGET_INSTALLMENT,
    render: (x: Offer) => `<span class="font-weight-medium">${numeralFormat(x.overview.lastInstallment)} ${currentCurrency}</span>`,
    open: false,
    children: [
      {
        header: () => I18NGetter().useHeaderContent.FIRST_INSTALLMENT,
        key: OfferDataTableElementKey.TARGET_INSTALLMENT_INSTALLMENT,
        render: (x: Offer, userInput?: Nullable<SimulationDemand>) => (
          `${numeralFormat(x.monthlyFees.lastInstallment)} ${currentCurrency}` +
          (x.fxValues ? ` (${x.fxValues.lastInstallment} ${userInput?.currency})` : '')
        ),
      },
      {
        header: () => I18NGetter().useHeaderContent.REAL_ESTATE_INSURANCE,
        key: OfferDataTableElementKey.TARGET_INSTALLMENT_REAL_ESTATE_INSURANCE,
        render: (x: Offer, userInput?: Nullable<SimulationDemand>) =>
          renderInsurance(x, 'realEstateInsurance', userInput?.loanPeriodInMonths),
      },
      {
        header: () => I18NGetter().useHeaderContent.LIFE_INSURANCE,
        key: OfferDataTableElementKey.TARGET_INSTALLMENT_LIFE_INSURANCE,
        render: (x: Offer, userInput?: Nullable<SimulationDemand>) =>
          renderInsurance(x, 'lifeInsurance', userInput?.loanPeriodInMonths),
      },
      {
        header: () => I18NGetter().useHeaderContent.PAYMENT_INSURANCE,
        key: OfferDataTableElementKey.TARGET_INSTALLMENT_PAYMENT_INSURANCE,
        render: (x: Offer, userInput?: Nullable<SimulationDemand>) =>
          renderInsurance(x, 'insurancePackage', userInput?.loanPeriodInMonths),
      },
      {
        header: () => I18NGetter().useHeaderContent.INSURANCE_PACKAGE,
        key: OfferDataTableElementKey.TARGET_INSTALLMENT_INSURANCE_PACKAGE,
        render: (x: Offer, userInput?: Nullable<SimulationDemand>) =>
          renderInsurance(x, 'insurancePackage', userInput?.loanPeriodInMonths),
      },
      {
        header: () => I18NGetter().useHeaderContent.BANK_ACCOUNT,
        key: OfferDataTableElementKey.TARGET_INSTALLMENT_COSTS_SUM_PERSONAL_ACCOUNT,
        render: (x: Offer) => x.additionalProducts?.bankAccount?.fee ? `${numeralFormat(x.additionalProducts?.bankAccount.fee)} ${currentCurrency}` : '-',
      },
    ],
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_PRINCIPAL_AND_INTEREST_INSTALLMENT,
    key: OfferDataTableElementKey.PRINCIPAL_AND_INTEREST_INSTALLMENT,
    height: TableElementHeight.INPUT,
    headerClass: mortgageCalculatorTour.step.PRINCIPAL_AND_INTEREST_INSTALLMENT,
    render: (x: Offer) => `<span class="font-weight-medium">${numeralFormat(x.monthlyFees.lastInstallment)} ${currentCurrency}</span>`,
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_NON_MONTHLY_COSTS,
    key: OfferDataTableElementKey.NON_MONTHLY_COSTS,
    headerClass: mortgageCalculatorTour.step.NON_MONTHLY_COSTS,
    open: false,
    runtimeTemplate: true,
    render: (x: Offer) => x.nonMonthlyFees.realEstateInsurance?.[0]?.fee || x.nonMonthlyFees.creditCard?.fee
      ? I18NGetter().useHeaderContent.YES : I18NGetter().useHeaderContent.NO,
    children: [
      {
        header: () => I18NGetter().useHeaderContent.REAL_ESTATE_INSURANCE,
        key: OfferDataTableElementKey.NON_MONTHLY_COSTS_REAL_ESTATE_INSURANCE,
        runtimeTemplate: true,
        render: (x: Offer) => x.nonMonthlyFees.realEstateInsurance?.[0]?.fee ? `${numeralFormat(x.nonMonthlyFees.realEstateInsurance?.[0]?.fee)} ${currentCurrency}` : EMPTY_VALUE,
      },
      {
        header: () => I18NGetter().useHeaderContent.CREDIT_CARD,
        key: OfferDataTableElementKey.NON_MONTHLY_COSTS_CREDIT_CARD,
        render: (x: Offer) => x.nonMonthlyFees.creditCard?.fee ? `${numeralFormat(x.nonMonthlyFees.creditCard?.fee)} ${currentCurrency}` : EMPTY_VALUE,
      },
    ],
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_COSTS_SUM,
    key: OfferDataTableElementKey.TOTAL_COSTS_SUM,
    groupKey: OfferDataTableGroupKey.BASIC_PARAMETERS_GROUP,
    height: TableElementHeight.DOUBLE_DEFAULT,
    open: false,
    render: (x: Offer) => `${numeralFormat(x.totals.costsSum)} ${currentCurrency}`,
    condition: (userInput: Nullable<SimulationDemand>, isPrintMode?: boolean): boolean => !!isPrintMode,
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_COSTS_SUM,
    key: OfferDataTableElementKey.COSTS_SUM,
    open: false,
    render: (x: Offer) => `${numeralFormat(x.totals.costsSum)} ${currentCurrency}`,
    children: [
      {
        key: OfferDataTableElementKey.COSTS_SUM_INTEREST,
        header: () => I18NGetter().useHeaderContent.TOTAL_INTEREST,
        render: (x: Offer, userInput?: Nullable<SimulationDemand>) => x.fxValues
          ? `${numeralFormat(x.totals.interest)} ${currentCurrency} (${numeralFormat(x.fxValues.interest)} ${userInput?.currency})`
          : `${numeralFormat(x.totals.interest)} ${currentCurrency}`,
      },
      {
        key: OfferDataTableElementKey.COSTS_SUM_PROVISION,
        header: () => I18NGetter().useHeaderContent.COMMISSION,
        render: (x: Offer) => x.creditedCosts.includes(Cost.PROVISION)
          ? I18NGetter().useMortgageTableElements.CREDITED_INITIAL_COSTS
          : `${numeralFormat(x.totals.provision)} ${currentCurrency}`,
      },
      {
        key: OfferDataTableElementKey.COSTS_SUM_BRIDGING_INSURANCE,
        header: () => I18NGetter().useHeaderContent.BRIDGING_INSURANCE,
        render: (x: Offer) => `${numeralFormat(x.totals.bridgingInsurance)} ${currentCurrency}`,
      },
      {
        key: OfferDataTableElementKey.COSTS_SUM_LOW_PERSONAL_SHARE_INSURANCE,
        header: () => I18NGetter().useHeaderContent.LOW_PERSONAL_SHARE_INSURANCE,
        render: (x: Offer) => `${numeralFormat(x.totals.lowPersonalShareInsurance)} ${currentCurrency}`,
      },
      {
        key: OfferDataTableElementKey.COSTS_SUM_REAL_ESTATE_INSURANCE,
        header: () => I18NGetter().useHeaderContent.REAL_ESTATE_INSURANCE,
        render: (x: Offer) => `${numeralFormat(x.totals.realEstateInsurance)} ${currentCurrency}`,
      },
      {
        key: OfferDataTableElementKey.COSTS_SUM_REAL_LIFE_INSURANCE,
        header: () => I18NGetter().useHeaderContent.LIFE_INSURANCE,
        render: (x: Offer) => `${numeralFormat(x.totals.lifeInsurance)} ${currentCurrency}`,
      },
      {
        key: OfferDataTableElementKey.COSTS_SUM_PAYMENT_INSURANCE,
        header: () => I18NGetter().useHeaderContent.PAYMENT_INSURANCE,
        render: (x: Offer) => `${numeralFormat(x.totals.paymentInsurance)} ${currentCurrency}`,
      },
      {
        key: OfferDataTableElementKey.COSTS_SUM_INSURANCE_PACKAGE,
        header: () => I18NGetter().useHeaderContent.INSURANCE_PACKAGE,
        render: (x: Offer) => `${numeralFormat(x.totals.insurancePackage)} ${currentCurrency}`,
      },
      {
        key: OfferDataTableElementKey.COSTS_SUM_PERSONAL_ACCOUNT,
        header: () => I18NGetter().useHeaderContent.BANK_ACCOUNT,
        render: (x: Offer) => `${numeralFormat(x.totals.bankAccount)} ${currentCurrency}`,
      },
      {
        key: OfferDataTableElementKey.COSTS_SUM_CREDIT_CARD,
        header: () => I18NGetter().useHeaderContent.CREDIT_CARD,
        render: (x: Offer) => `${numeralFormat(x.totals.creditCard)} ${currentCurrency}`,
      },
      {
        key: OfferDataTableElementKey.COSTS_SUM_REAL_ESTATE_VALUATION,
        header: () => I18NGetter().useHeaderContent.REAL_ESTATE_APPRAISAL,
        render: (x: Offer) => `${numeralFormat(x.totals.realEstateAppraisal)} ${currentCurrency}`,
      },
    ],
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_LTV,
    key: OfferDataTableElementKey.LTV,
    groupKey: OfferDataTableGroupKey.BASIC_PARAMETERS_GROUP,
    render: (x: Offer, userInput?: Nullable<SimulationDemand>) => `${Math.round(x.totals.capital * 100 / (userInput as MortgageSimulationDemand)?.hypothecValue!)} %`,
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_TOTAL_SUM,
    key: OfferDataTableElementKey.TOTAL_SUM,
    groupKey: OfferDataTableGroupKey.BASIC_PARAMETERS_GROUP,
    render: (x: Offer) => `${numeralFormat(x.totals.totalSum)} ${currentCurrency}`,
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_RRSO,
    key: OfferDataTableElementKey.RRSO,
    groupKey: OfferDataTableGroupKey.BASIC_PARAMETERS_GROUP,
    render: (x: Offer) => `${x.rrso} %`,
    condition: ableToRenderRrso,
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_LOAN_AMOUNT_GROSS,
    key: OfferDataTableElementKey.LOAN_AMOUNT_GROSS,
    groupKey: OfferDataTableGroupKey.BASIC_PARAMETERS_GROUP,
    render: (x: Offer, userInput?: Nullable<SimulationDemand>) =>
      `${numeralFormat(x.totals.capital)} ${currentCurrency}` + (x.fxValues ? ` (${numeralFormat(x.fxValues.loanAmount)} ${userInput?.currency})` : ''),
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_LOAN_AMOUNT,
    height: TableElementHeight.INPUT,
    printHeight: TableElementHeight.DEFAULT,
    key: OfferDataTableElementKey.LOAN_AMOUNT,
    groupKey: OfferDataTableGroupKey.BASIC_PARAMETERS_GROUP,
    printRender: (x: Offer, userInput?: Nullable<SimulationDemand>) => `${numeralFormat(x.customizations?.customLoanAmount || userInput?.loanAmount || 0)} ${currentCurrency}`,
    runtimeTemplate: true,
    render: () => `
              <div>
                  <fp-input
                    :suffix="$env.currentCurrency()"
                    :value="customLoanAmount"
                    type="number"
                    int
                    @input="onCustomAmountChange"
                    class="loan-amount-input"
                    dense/>
              </div>
`,
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_LOAN_PERIOD,
    height: TableElementHeight.DOUBLE_INPUT,
    printHeight: TableElementHeight.DEFAULT,
    key: OfferDataTableElementKey.LOAN_PERIOD,
    groupKey: OfferDataTableGroupKey.BASIC_PARAMETERS_GROUP,
    printRender: (x: Offer, userInput?: Nullable<SimulationDemand>) => `${numeralFormat(x.customizations?.customLoanPeriodInMonths || userInput?.loanPeriodInMonths || 0)} ${I18NGetter().useTimeCommons.MONTHS_SHORT}`,
    runtimeTemplate: true,
    render: () => `
              <div class="d-flex flex-column">
                    <fp-input
                    class="loan-period-input"
                    type="number"
                    @input="onCustomPeriodChange"
                    :value="customLoanPeriod"
                    suffix="${I18NGetter().useMortgageTableElements.MONTHS}"
                    int
                    validate-on-blur
                    dense>
                    </fp-input>
                    <div v-if="offer.restrictions?.loanPeriodRestrictions" class="d-flex justify-space-between body-2">
                        <span>${I18NGetter().useMortgageTableElements.MIN} {{offer.restrictions.loanPeriodRestrictions.minLoanPeriod}}</span>
                        <span>${I18NGetter().useMortgageTableElements.MAX} {{offer.restrictions.loanPeriodRestrictions.maxLoanPeriod}}</span>
                    </div>
              </div>`,
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_DESCRIPTION,
    key: OfferDataTableElementKey.DESCRIPTION,
    height: TableElementHeight.EXTRA_LARGE,
    printHeight: 1250,
    class: 'd-block text-wrap overflow-y-auto',
    runtimeTemplate: true,
    render: () => '<fp-markdown v-once class="text-left print-font-size-10 caption" :source="offer.info.description"></fp-markdown>',
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_TRANCHE_ACCOUNTING,
    key: OfferDataTableElementKey.TRANCHE_ACCOUNTING,
    height: TableElementHeight.LARGE,
    printHeight: TableElementHeight.EXTRA_EXTRA_LARGE,
    class: 'd-block text-wrap overflow-y-auto',
    runtimeTemplate: true,
    render: () => '<fp-markdown class="font-weight-regular print-font-size-10 caption" v-once :source="offer.info.trancheAccountingDescription"></fp-markdown>',
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_PERCENTAGE_FOR_WHICHEVER_EXPENSE,
    key: OfferDataTableElementKey.PERCENTAGE_FOR_WHICHEVER_EXPENSE,
    open: false,
    height: TableElementHeight.INPUT,
    render: (x: Offer) => `${numeralFormat(x.anyPurposeExpense.amount)} ${currentCurrency} / ${x.anyPurposeExpense.percentage} %`,
    children: [
      {
        header: () => '',
        key: OfferDataTableElementKey.PERCENTAGE_FOR_WHICHEVER_EXPENSE_DESCRIPTION,
        height: 200,
        class: 'd-block text-wrap overflow-y-auto body-2 text--secondary',
        runtimeTemplate: true,
        render: () => `
          <div class="pa-2 text-left font-weight-regular print-font-size-10 caption">
            <span>${I18NGetter().useMortgageTableElements.PERCENTAGE_FOR_WHICHEVER_EXPENSE_DESCRIPTION}</span>
            <p class="mt-1 mb-0" v-if="offer.info.percentageForWhicheverExpenseDescription">
                {{offer.info.percentageForWhicheverExpenseDescription}}
            </p>
          </div>`,
      },
    ],
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_EARLIER_PAYMENT_DESCRIPTION,
    key: OfferDataTableElementKey.EARLIER_PAYMENT_DESCRIPTION,
    height: 135,
    class: 'd-block text-wrap overflow-y-auto caption',
    runtimeTemplate: true,
    render: (x: Offer) => x.info.earlierPaymentDescription
      ? '<fp-markdown v-once class="text-left print-font-size-10 caption" :source="offer.info.earlierPaymentDescription"></fp-markdown>'
      : '<span class="text-center">-</span>',
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_CONVERSION,
    key: OfferDataTableElementKey.CONVERSION,
    height: TableElementHeight.INPUT,
    class: 'd-block text-wrap overflow-y-auto caption',
    runtimeTemplate: true,
    render: (x: Offer) => x.info.conversionDescription ? `<p class="mb-0">${x.info.conversionDescription}</p>`
      : '<span class="d-flex justify-center">-</span>',
  },
  MarginRow,
  Provision,
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_CURRENCY_SPREAD,
    key: OfferDataTableElementKey.CURRENCY_SPREAD,
    open: false,
    height: TableElementHeight.INPUT,
    condition: isForeignCurrency,
    render: (x: Offer) => `${x.currencySpread} %`,
    children: [
      {
        header: () => '',
        key: OfferDataTableElementKey.CURRENCY_SPREAD_DESCRIPTION,
        height: TableElementHeight.LARGE,
        class: 'd-block text-wrap overflow-y-auto body-2 text--secondary',
        render: () => {
          return `
          <div class="pa-2 text-left font-weight-regular">
            <p class="mb-0">${I18NGetter().useHeaderContent.CURRENCY_SPREAD}</p>
          </div>`;
        },
      },
    ],
  },
  {
    header: () => 'Informacje o programie',
    key: OfferDataTableElementKey.GOV_PROGRAM,
    open: false,
    condition: () => EnvironmentService.Environment.isAppDomain().LENDI_PL && currentCurrency === 'PLN',
    runtimeTemplate: true,
    height: TableElementHeight.EXTRA_LARGE,
    class: 'd-block text-wrap overflow-y-auto caption',
    render: getGovProgramInfo,
  },
  {
    header: () => 'Suma dopłat',
    key: OfferDataTableElementKey.TOTAL_SUBSIDY,
    open: false,
    condition: () => EnvironmentService.Environment.isAppDomain().LENDI_PL && currentCurrency === 'PLN',
    runtimeTemplate: true,
    height: TableElementHeight.DEFAULT,
    class: 'd-block text-wrap overflow-y-auto text-center align-center',
    render: getTotalSubsidyRow,
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_BUY_RATE,
    key: OfferDataTableElementKey.BUY_RATE,
    open: false,
    height: TableElementHeight.INPUT,
    condition: isForeignCurrency,
    render: (x: Offer) => `${x.fxValues?.buyRate} ${currentCurrency}`,
  },
  InterestRateSum,
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_ADDITIONAL_PRODUCTS,
    key: OfferDataTableElementKey.ADDITIONAL_PRODUCTS,
    open: false,
    render: (x: Offer) => `${x.features.noXsell ? I18NGetter().useHeaderContent.NO : I18NGetter().useHeaderContent.YES}`,
  },
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_PERSONAL_ACCOUNT,
    key: OfferDataTableElementKey.PERSONAL_ACCOUNT,
    groupKey: OfferDataTableGroupKey.X_SELL_GROUP,
    height: TableElementHeight.EXTRA_LARGE,
    printHeight: 110,
    runtimeTemplate: true,
    class: 'd-block text-wrap overflow-y-auto',
    render: (x: Offer) => x.additionalProducts?.bankAccount ? `
            <div class="pa-1">
             <span class="primary--text font-weight-medium body-1 d-flex align-center"><v-icon class="pr-2 primary--text">mdi-information-outline</v-icon>${I18NGetter().useHeaderContent.REQUIRED}</span>
              <p class="mb-1 font-weight-medium body-1">${x.additionalProducts?.bankAccount.name}</p>
              <p class="mb-1 font-weight-medium">${x.additionalProducts?.bankAccount.fee}${currentCurrency} / ${I18NGetter().useHeaderContent.MONTHLY}</p>
              <p class="caption mb-1">${x.additionalProducts?.bankAccount.description}</p>
            </div>
            ` : `
            <div>
                <span class="error--text font-weight-medium body-1">${I18NGetter().useHeaderContent.NOT_REQUIRED}</span>
            </div>
            `,
    printRender: (x: Offer) => x.additionalProducts?.bankAccount ? `
            <div class="pa-1">
             <span class="primary--text font-weight-medium print print-font-size-11 d-flex align-center"><v-icon class="pr-1 primary--text body-1">mdi-information-outline</v-icon>${I18NGetter().useHeaderContent.REQUIRED}</span>
              <p class="mb-1 font-weight-medium print-font-size-11">${x.additionalProducts?.bankAccount.name}</p>
              <p class="mb-1 font-weight-medium print-font-size-11">${x.additionalProducts?.bankAccount.fee}${currentCurrency} / ${I18NGetter().useHeaderContent.MONTHLY}</p>
              <p class="mb-1 font-weight-regular">${x.additionalProducts?.bankAccount.description}</p>
            </div>
            ` : `
            <div>
                <span class="error--text font-weight-medium body-2">${I18NGetter().useHeaderContent.NOT_REQUIRED}</span>
            </div>
            `,
  },
  CreditCard,
  getInsuranceElement(
    OfferDataTableElementKey.BRIDGING_INSURANCE,
    () => I18NGetter().useMortgageTableElements.HEADER_BRIDGING_INSURANCE,
    Cost.BRIDGING_INSURANCE,
    OfferDataTableGroupKey.X_SELL_GROUP,
    () => EnvironmentService.Environment.isAppDomain().LENDI_PL,
  ),
  getInsuranceElement(
    OfferDataTableElementKey.REAL_ESTATE_INSURANCE,
    () => I18NGetter().useMortgageTableElements.HEADER_REAL_ESTATE_INSURANCE,
    Cost.REAL_ESTATE_INSURANCE,
    OfferDataTableGroupKey.X_SELL_GROUP
  ),
  getInsuranceElement(
    OfferDataTableElementKey.LIFE_INSURANCE,
    () => I18NGetter().useMortgageTableElements.HEADER_LIFE_INSURANCE,
    Cost.LIFE_INSURANCE,
    OfferDataTableGroupKey.X_SELL_GROUP
  ),
  getInsuranceElement(
    OfferDataTableElementKey.PAYMENT_INSURANCE,
    () => I18NGetter().useMortgageTableElements.HEADER_PAYMENT_INSURANCE_UNEMPLOYMENT,
    Cost.PAYMENT_INSURANCE,
    OfferDataTableGroupKey.X_SELL_GROUP
  ),
  getInsuranceElement(
    OfferDataTableElementKey.LOW_PERSONAL_SHARE_INSURANCE,
    () => I18NGetter().useMortgageTableElements.HEADER_LOW_PERSONAL_SHARE_INSURANCE,
    Cost.LOW_PERSONAL_SHARE_INSURANCE,
    OfferDataTableGroupKey.X_SELL_GROUP
  ),
  getInsuranceElement(
    OfferDataTableElementKey.INSURANCE_PACKAGE,
    () => I18NGetter().useMortgageTableElements.HEADER_INSURANCE_PACKAGE,
    Cost.INSURANCE_PACKAGE,
    OfferDataTableGroupKey.X_SELL_GROUP
  ),
  {
    header: () => I18NGetter().useMortgageTableElements.HEADER_ADDITIONAL_PRODUCTS_INFO,
    key: OfferDataTableElementKey.ADDITIONAL_PRODUCTS_INFO,
    groupKey: OfferDataTableGroupKey.X_SELL_GROUP,
    height: TableElementHeight.EXTRA_LARGE,
    printHeight: TableElementHeight.EXTRA_EXTRA_LARGE,
    class: 'd-block text-wrap overflow-y-auto caption',
    runtimeTemplate: true,
    render: () => '<fp-markdown class="font-weight-regular print-font-size-10 caption" v-once :source="offer.info.xsellDescription"></fp-markdown>',
  },
].filter(element => !EnvironmentService.Environment.getAppDomainConfig().calculator.excludedSimulationRows.productTypeMortgage
  .includes(element.key)
)
  .map(element => element.children
    ? ({
      ...element,
      children: element.children.filter(
        child => !EnvironmentService.Environment.getAppDomainConfig().calculator.excludedSimulationRows.productTypeMortgage
          .includes(child.key)
      ),
    }) : element);
